import React, { useState, useEffect, useCallback } from 'react';
import { VersionMetadata, formatVersionType } from '../../types/productVersion';
import { productVersionService } from '../../services/ProductVersionService';
import { productService } from '../../services/ProductService';
import { Button } from '../ui/button';
import { History, RotateCcw, ExternalLink, ChevronDown } from 'lucide-react';
import { ConfirmDialog } from '../dialogs/ConfirmDialog';
import { PublishToShopifyButton } from '../PublishToShopifyButton';

interface ProductVersionHistoryProps {
  productId: string;
  onVersionRestored?: () => void;
}

export function ProductVersionHistory({ productId, onVersionRestored }: ProductVersionHistoryProps) {
  const [versions, setVersions] = useState<VersionMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState<{id: string; number: number} | null>(null);

  const loadVersions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const versionList = await productVersionService.getVersionMetadata(productId);
      setVersions(versionList);
    } catch (err) {
      setError('Failed to load version history');
      console.error('Error loading versions:', err);
    } finally {
      setLoading(false);
    }
  }, [productId, setLoading, setError, setVersions]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  const handleRestore = async () => {
    if (!versionToRestore) return;

    try {
      setRestoring(true);
      setError(null);
      await productService.restoreVersion(versionToRestore.id);
      if (onVersionRestored) {
        onVersionRestored();
      }
      setVersionToRestore(null);
    } catch (err) {
      setError('Failed to restore version');
      console.error('Error restoring version:', err);
    } finally {
      setRestoring(false);
    }
  };

  if (loading) {
    return <div className="text-center text-gray-500">Loading version history...</div>;
  }

  if (error) {
    return (
      <div className="text-center text-red-500">
        {error}
        <Button
          variant="outline"
          size="sm"
          onClick={loadVersions}
          className="mt-2"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-6 border rounded-lg">
      <div 
        className="flex items-center gap-2 p-3 bg-gray-50 rounded-t-lg cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <History className="h-5 w-5 text-gray-500" />
        <h3 className="text-lg font-semibold">Version History</h3>
        <ChevronDown 
          className={`h-5 w-5 text-gray-500 ml-auto transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
        />
      </div>

      <div 
        className={`overflow-hidden transition-all duration-200 ease-in-out ${
          isOpen ? 'max-h-[400px] border-t' : 'max-h-0'
        }`}
      >
        <div className="p-3 space-y-3 overflow-y-auto">
          {versions.map((version) => (
            <div
              key={`${version.versionNumber}-${version.timestamp}`}
              className="border rounded-lg p-3 bg-white"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium">
                    Version {version.versionNumber}
                  </div>
                  <div className="text-sm text-gray-500">
                    {formatVersionType(version.type, version.marketplace)}
                  </div>
                  <div className="text-sm text-gray-500">
                    {version.timestamp.toLocaleString()}
                  </div>
                  {version.notes && (
                    <div className="text-sm text-gray-600 mt-1">
                      {version.notes}
                    </div>
                  )}
                </div>
                {version.type === 'import' && version.versionNumber === 1 && version.data ? (
                  <PublishToShopifyButton
                    storeName={version.data.storeName}
                    productId={version.data.links?.shopify ? version.data.links.shopify.split('/products/')[1] : ''}
                    documentId={productId}
                    title={version.data.basicInfo.title}
                    description={version.data.basicInfo.description}
                    bulletPoints={version.data.basicInfo.bulletPoints}
                    pageTitle={version.data.basicInfo.pageTitle}
                    metaDescription={version.data.basicInfo.metaDescription}
                    onPublishComplete={onVersionRestored}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 border border-blue-600 rounded-md ml-4"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Publish
                  </PublishToShopifyButton>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setVersionToRestore({ id: version.id, number: version.versionNumber })}
                    disabled={restoring}
                    className="ml-4"
                  >
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Restore
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!versionToRestore}
        onClose={() => setVersionToRestore(null)}
        onConfirm={handleRestore}
        title="Restore Version"
        description={`Are you sure you want to restore Version ${versionToRestore?.number}?`}
        confirmText="Restore"
        cancelText="Cancel"
      />
    </div>
  );
}